import { useSelector } from 'react-redux';
import { authSelector } from '../modules/auth/state/authSlice';
import {
  UserIcon,
  ArrowLeftStartOnRectangleIcon,
  BookOpenIcon,
  ChatBubbleOvalLeftIcon,
  GlobeEuropeAfricaIcon,
  UserCircleIcon,
  Bars3Icon
} from '@heroicons/react/24/outline';
import { Menu } from './Menu';
import { useLogout } from '../modules/auth/hooks/useLogout';
import clsx from 'clsx';
import libraryIcon from '../assets/images/library.png';
import { Link, NavLink } from 'react-router-dom';
import { Popover, PopoverButton, PopoverPanel, useClose } from '@headlessui/react';
import { ExclamationCircleIcon } from '@heroicons/react/16/solid';

type HeaderProps = {
  isConnected?: boolean;
  totalUnread?: number;
};

export const LogoIcon = () => (
  <Link to="/">
    <div className="flex items-center gap-1 relative text-white">
      <img src={libraryIcon} alt="SellBooks AI" className="w-6" />
      <h1 className="font-bold">SellBooks AI</h1>
      <div className="p-1 text-[.6rem] py-0 text-white bg-cyan-800 border border-white rounded-md">
        <span>Beta</span>
      </div>
    </div>
  </Link>
);

export const Header = ({ isConnected, totalUnread = 0 }: HeaderProps) => {
  const { token, user } = useSelector(authSelector);
  const isAuthenticated = Boolean(token);
  const { logoutUser } = useLogout();
  return (
    <div className="bg-cyan-800 text-white w-full sticky top-0 z-50">
      <div className="max-w-screen-xl mx-auto flex items-center justify-between w-full p-4">
        <LogoIcon />

        <div className="flex md:hidden">
          <Popover className="relative">
            <PopoverButton>
              <Bars3Icon className="size-8 stroke-white" />
            </PopoverButton>
            <PopoverPanel
              anchor="right"
              className="flex flex-col bg-cyan-800  gap-2 z-[100] p-4 rounded-lg shadow-lg transition duration-800 left-0 mt-8 shadow-xl border border-white mr-4">
              {isAuthenticated ? (
                <>
                  <NavButton
                    to="/books"
                    text="Explore"
                    icon={<GlobeEuropeAfricaIcon className="size-6 stroke-white" />}
                  />
                  <NavButton
                    to="/my-books"
                    text="My books"
                    icon={<BookOpenIcon className="size-6 stroke-white" />}
                  />
                  <div className="relative">
                    {Boolean(totalUnread) && (
                      <span className="text-[.6rem] font-bold rounded-full right-0 -top-1 w-4 h-4 absolute flex items-center justify-center bg-red-500">
                        {totalUnread}
                      </span>
                    )}
                    <NavButton
                      to="/chat"
                      text="Messages"
                      icon={<ChatBubbleOvalLeftIcon className="size-6 stroke-white" />}
                    />
                  </div>
                  <NavButton
                    to="/profile"
                    text="Profile"
                    icon={<UserCircleIcon className="size-6 stroke-white" />}
                  />
                  <button
                    key={'delete'}
                    onClick={logoutUser}
                    className="flex items-center gap-2 rounded-md py-1.5 px-3 text-lg font-semibold text-red-500 focus:outline-none hover:bg-cyan-700">
                    <ArrowLeftStartOnRectangleIcon className="size-6 stroke-red-500" />
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <NavButton
                    to="/books"
                    text="Explore"
                    icon={<GlobeEuropeAfricaIcon className="size-4 stroke-white" />}
                  />
                  <NavButton
                    to="/login"
                    text="SignIn"
                    icon={<UserIcon className="size-4 stroke-white" />}
                  />
                </>
              )}
            </PopoverPanel>
          </Popover>
        </div>

        <div className="items-center gap-1 hidden md:flex">
          {isAuthenticated ? (
            <>
              <NavButton
                to="/books"
                text="Explore"
                icon={<GlobeEuropeAfricaIcon className="size-4 stroke-white" />}
              />
              <NavButton
                to="/my-books"
                text="My books"
                icon={<BookOpenIcon className="size-4 stroke-white" />}
              />
              <div className="relative">
                {Boolean(totalUnread) && (
                  <span className="text-[.6rem] font-bold rounded-full right-0 -top-1 w-4 h-4 absolute flex items-center justify-center bg-red-500">
                    {totalUnread}
                  </span>
                )}
                <NavButton
                  to="/chat"
                  text="Messages"
                  icon={<ChatBubbleOvalLeftIcon className="size-4 stroke-white" />}
                />
              </div>

              <div className="text-right z-10">
                <Menu
                  buttonText={
                    <div className="flex items-center gap-2">
                      <div
                        className={clsx(
                          'w-2 h-2 rounded-full',
                          isConnected ? 'bg-green-500' : 'bg-red-500'
                        )}
                      />
                      <p>
                        {user?.first_name} {user?.last_name}
                      </p>
                    </div>
                  }
                  dividersIndex={[2]}
                  items={[
                    <p
                      key="asdas"
                      className="group flex w-full items-center text-lg font-bold gap-2 px-3">
                      {user?.first_name} {user?.last_name}
                    </p>,
                    <p
                      key="asdas"
                      className="group flex w-full items-center gap-2 text-xs py-1.5 px-3">
                      {user?.email}
                    </p>,
                    <Link
                      to="/profile"
                      key="oteher"
                      className="group flex w-full items-center gap-2 rounded-lg py-1.5 px-3 data-[focus]:bg-white/10">
                      <UserCircleIcon className="size-5 stroke-black" />
                      Profile
                    </Link>,
                    <button
                      key={'delete'}
                      onClick={logoutUser}
                      className="group text-red-500 flex w-full items-center gap-2 rounded-lg py-1.5 px-3 data-[focus]:bg-white/10">
                      <ArrowLeftStartOnRectangleIcon className="size-5 stroke-red-600" />
                      Sign out
                    </button>
                  ]}
                />
              </div>
            </>
          ) : (
            <>
              <NavButton
                to="/books"
                text="Explore"
                icon={<GlobeEuropeAfricaIcon className="size-4 stroke-white" />}
              />
              <NavButton
                to="/login"
                text="SignIn"
                icon={<UserIcon className="size-4 stroke-white" />}
              />
            </>
          )}
        </div>
      </div>
      {!isAuthenticated && (
        <div className="w-full bg-yellow-300 text-black text-center p-2 text-sm font-semibold flex items-center gap-2 justify-center">
          <ExclamationCircleIcon className="inline-block w-4 h-4 text-yellow-600" />
          <p>
            SellBooks AI is 100% free to use while in beta. Don't miss out on the opportunity to
            publish your books for free!
          </p>
        </div>
      )}
    </div>
  );
};

type NavButtonProps = {
  to: string;
  text: string;
  icon?: React.ReactNode;
};
const NavButton = ({ to, text, icon }: NavButtonProps) => {
  const close = useClose();

  return (
    <NavLink
      onClick={close}
      className={({ isActive }) =>
        clsx(
          'inline-flex items-center gap-1 rounded-md py-1.5 px-3 text-lg md:text-sm/6 font-semibold text-white focus:outline-none hover:bg-cyan-700 data-[focus]:outline-1 data-[focus]:outline-white',
          isActive ? 'bg-cyan-700' : 'bg-cyan-800'
        )
      }
      to={to}>
      {icon}
      {text}
    </NavLink>
  );
};
